import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  X,
  ArrowRight,
  TrendingDown,
  Sparkles,
  Zap,
  Globe,
  HeartHandshake,
  ShieldCheck,
  Clock,
  Eye,
  MapPin,
  Wallet,
  CalendarClock,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";

/* =====================================================================
   Comparison rows, "Conciergerie classique" vs Terre et Mer
   ===================================================================== */
const COMPARISON = [
  {
    icon: Wallet,
    label: "Commission",
    classique: "20 % à 25 % TTC",
    classiqueBad: true,
    nous: "15 % TTC",
    detail: "Aucun frais caché. Vous gardez la majorité des revenus.",
  },
  {
    icon: Globe,
    label: "Présence",
    classique: "Agence physique avec horaires limités",
    classiqueBad: true,
    nous: "100 % digital + équipe locale",
    detail: "Tableau de bord en ligne 24h/24, City Manager sur le terrain.",
  },
  {
    icon: Zap,
    label: "Réactivité",
    classique: "Lenteur, réponse en 24-48 h ouvrées",
    classiqueBad: true,
    nous: "Service 24h/24 · 7j/7 (permanence de nuit)",
    detail: "Messages voyageurs traités en continu, intervention en 30 min.",
  },
  {
    icon: Eye,
    label: "Transparence",
    classique: "Frais opaques, suppléments fréquents",
    classiqueBad: true,
    nous: "Tarif unique 15 % · 0 € caché",
    detail: "Vous encaissez DIRECTEMENT les revenus. Facture mensuelle simple.",
  },
  {
    icon: Sparkles,
    label: "Photos & annonce",
    classique: "Photos amateures, descriptifs basiques",
    classiqueBad: true,
    nous: "Shooting pro + storytelling éditorial",
    detail: "Drone, lumière dorée, livret d'accueil personnalisé.",
  },
  {
    icon: HeartHandshake,
    label: "Sélection des voyageurs",
    classique: "Acceptation automatique des demandes",
    classiqueBad: true,
    nous: "Vérification avis + identité",
    detail: "Nous filtrons activement pour préserver votre logement.",
  },
  {
    icon: ShieldCheck,
    label: "Sécurité du bien",
    classique: "Aucune caution sur Booking / Abritel",
    classiqueBad: true,
    nous: "Empreinte CB + AirCover 1 M€",
    detail: "400-1 500 € selon taille du bien. Couverture maximale Airbnb.",
  },
  {
    icon: MapPin,
    label: "Couverture géographique",
    classique: "1 secteur, peu de proximité",
    classiqueBad: true,
    nous: "30+ communes 35 & 22",
    detail: "Saint-Malo, Dinard, Cancale, Dinan, Saint-Cast, Lancieux…",
  },
  {
    icon: Clock,
    label: "Vitesse de mise en ligne",
    classique: "2 à 4 semaines",
    classiqueBad: true,
    nous: "24h",
    detail: "Photos pro, annonce optimisée et multidiffusion en moins de 24h.",
  },
  {
    icon: CalendarClock,
    label: "Engagement",
    classique: "Contrat 12 mois minimum",
    classiqueBad: true,
    nous: "Sans engagement",
    detail: "Vous pouvez partir à tout moment, c'est notre conviction.",
  },
];

/* =====================================================================
   Interactive savings simulator
   ===================================================================== */
const SavingsSimulator = () => {
  const [revenuAnnuel, setRevenuAnnuel] = useState(20000);
  const [tauxConcurrent, setTauxConcurrent] = useState(22);

  const { commissionConcurrent, commissionTM, economie, economieAnnees } = useMemo(() => {
    const cConcurrent = (revenuAnnuel * tauxConcurrent) / 100;
    const cTM = (revenuAnnuel * 15) / 100;
    const eco = Math.max(0, cConcurrent - cTM);
    return {
      commissionConcurrent: Math.round(cConcurrent),
      commissionTM: Math.round(cTM),
      economie: Math.round(eco),
      economieAnnees: Math.round(eco * 5),
    };
  }, [revenuAnnuel, tauxConcurrent]);

  const fmt = (n) =>
    n.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

  return (
    <section
      className="py-24 lg:py-32 bg-ink text-white relative overflow-hidden"
      data-testid="savings-simulator"
    >
      <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-gold/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-rose-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-tm relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left, copy */}
          <div className="lg:col-span-5">
            <span className="overline text-gold inline-flex items-center gap-2">
              <TrendingDown size={12} strokeWidth={2} />
              Calculez votre économie
            </span>
            <h2 className="mt-5 font-serif font-light text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Combien vous fait
              <span className="italic text-gold"> perdre</span> votre conciergerie actuelle ?
            </h2>
            <p className="mt-6 font-sans text-base md:text-lg text-white/65 leading-relaxed">
              Glissez les curseurs ci-dessous pour comparer votre commission actuelle avec notre tarif unique de 15 %. Le résultat est immédiat, et souvent surprenant.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold mb-2">
                  Économie / an
                </p>
                <p
                  className="font-serif text-3xl lg:text-4xl text-gold tabular-nums leading-none"
                  data-testid="savings-yearly"
                >
                  {fmt(economie)}
                </p>
              </div>
              <div className="bg-gold text-ink rounded-2xl p-5 shadow-lift">
                <p className="text-[10px] uppercase tracking-[0.25em] text-ink/65 font-semibold mb-2">
                  Sur 5 ans
                </p>
                <p
                  className="font-serif text-3xl lg:text-4xl text-ink tabular-nums leading-none"
                  data-testid="savings-5years"
                >
                  {fmt(economieAnnees)}
                </p>
              </div>
            </div>

            <Link
              to="/contact"
              className="btn-accent group mt-8 inline-flex"
              data-testid="savings-cta"
            >
              Lancer mon étude gratuite
              <ArrowRight
                size={14}
                strokeWidth={2}
                className="ml-3 group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>

          {/* Right, sliders */}
          <div className="lg:col-span-7">
            <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-3xl p-7 md:p-10 shadow-lift">
              {/* Revenu annuel */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-3">
                  <label
                    htmlFor="revenu"
                    className="text-[11px] uppercase tracking-[0.25em] font-semibold text-white/70"
                  >
                    Revenu annuel locatif (brut)
                  </label>
                  <span
                    className="font-serif text-2xl text-gold tabular-nums"
                    data-testid="revenu-display"
                  >
                    {fmt(revenuAnnuel)}
                  </span>
                </div>
                <input
                  id="revenu"
                  type="range"
                  min="5000"
                  max="80000"
                  step="500"
                  value={revenuAnnuel}
                  onChange={(e) => setRevenuAnnuel(Number(e.target.value))}
                  data-testid="slider-revenu"
                  className="tm-slider w-full"
                />
                <div className="flex justify-between text-[10px] text-white/45 font-sans mt-2">
                  <span>5 000 €</span>
                  <span>80 000 €</span>
                </div>
              </div>

              {/* Taux concurrent */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-3">
                  <label
                    htmlFor="taux"
                    className="text-[11px] uppercase tracking-[0.25em] font-semibold text-white/70"
                  >
                    Commission de votre conciergerie actuelle
                  </label>
                  <span
                    className="font-serif text-2xl text-rose-400 tabular-nums"
                    data-testid="taux-display"
                  >
                    {tauxConcurrent} %
                  </span>
                </div>
                <input
                  id="taux"
                  type="range"
                  min="15"
                  max="35"
                  step="1"
                  value={tauxConcurrent}
                  onChange={(e) => setTauxConcurrent(Number(e.target.value))}
                  data-testid="slider-taux"
                  className="tm-slider w-full"
                />
                <div className="flex justify-between text-[10px] text-white/45 font-sans mt-2">
                  <span>15 %</span>
                  <span>35 %</span>
                </div>
              </div>

              {/* Comparison bars */}
              <div className="space-y-5 pt-6 border-t border-white/10">
                <BarRow
                  label="Avec votre conciergerie actuelle"
                  amount={commissionConcurrent}
                  total={Math.max(commissionConcurrent, commissionTM, 1)}
                  color="bg-rose-400"
                  textColor="text-rose-300"
                  fmt={fmt}
                  testId="bar-concurrent"
                />
                <BarRow
                  label="Avec Terre et Mer Conciergerie"
                  amount={commissionTM}
                  total={Math.max(commissionConcurrent, commissionTM, 1)}
                  color="bg-gold"
                  textColor="text-gold"
                  fmt={fmt}
                  testId="bar-tm"
                />
              </div>

              <p className="mt-7 text-[11px] text-white/50 font-sans leading-relaxed">
                Calcul indicatif basé sur le revenu brut annoncé. Notre commission de 15 % TTC est unique, transparente, et ne change pas avec le volume.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom range slider styling */}
      <style>{`
        .tm-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          background: linear-gradient(to right, rgba(255,255,255,0.08), rgba(255,255,255,0.16));
          border-radius: 999px;
          outline: none;
          cursor: pointer;
        }
        .tm-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #E0B872, #C79547);
          box-shadow: 0 4px 12px rgba(224,184,114,0.5), inset 0 1px 0 rgba(255,255,255,0.3);
          cursor: grab;
          border: 2px solid #fff;
          transition: transform 0.15s ease;
        }
        .tm-slider::-webkit-slider-thumb:hover { transform: scale(1.12); }
        .tm-slider::-webkit-slider-thumb:active { cursor: grabbing; transform: scale(1.05); }
        .tm-slider::-moz-range-thumb {
          width: 24px; height: 24px; border-radius: 50%;
          background: linear-gradient(135deg, #E0B872, #C79547);
          box-shadow: 0 4px 12px rgba(224,184,114,0.5);
          cursor: grab; border: 2px solid #fff;
        }
      `}</style>
    </section>
  );
};

const BarRow = ({ label, amount, total, color, textColor, fmt, testId }) => {
  const widthPct = (amount / total) * 100;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-sans text-white/85">{label}</p>
        <p className={`font-serif text-xl ${textColor} tabular-nums font-medium`}>
          {fmt(amount)}
        </p>
      </div>
      <div className="h-3 bg-white/8 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
};

/* =====================================================================
   Comparison table
   ===================================================================== */
const ComparisonTable = () => (
  <section
    className="py-24 lg:py-32 bg-cream"
    data-testid="comparison-table-section"
  >
    <div className="container-tm">
      <div className="max-w-3xl mb-16">
        <span className="overline">Le match en 10 critères</span>
        <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
          Conciergerie classique
          <span className="italic text-gold"> vs Terre et Mer</span>.
        </h2>
        <p className="mt-6 font-sans text-base md:text-lg text-ink/65 leading-relaxed">
          Sans citer de noms, voici ce que les propriétaires nous racontent quand ils nous rejoignent. Notre engagement : moins cher, plus rapide, plus humain.
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-3xl shadow-lift overflow-hidden border border-ink/5">
        {/* Header */}
        <div className="grid grid-cols-12 bg-ink text-white">
          <div className="col-span-4 px-7 py-6">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
              Critère
            </p>
          </div>
          <div className="col-span-4 px-7 py-6 border-l border-white/10">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold mb-1">
              Conciergerie classique
            </p>
            <p className="font-serif text-lg text-white/85">Le marché</p>
          </div>
          <div className="col-span-4 px-7 py-6 border-l border-white/10 bg-gold/15">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">
              Terre et Mer
            </p>
            <p className="font-serif text-lg text-white">Notre approche</p>
          </div>
        </div>

        {/* Rows */}
        {COMPARISON.map((row, i) => {
          const Icon = row.icon;
          return (
            <div
              key={row.label}
              data-testid={`comparison-row-${i}`}
              className={`grid grid-cols-12 ${
                i % 2 === 0 ? "bg-white" : "bg-cream/40"
              } border-t border-ink/5`}
            >
              <div className="col-span-4 px-7 py-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-sage/10 flex items-center justify-center shrink-0">
                  <Icon size={16} strokeWidth={1.6} className="text-sage" />
                </div>
                <div>
                  <p className="font-serif text-base text-ink leading-tight">
                    {row.label}
                  </p>
                  <p className="text-[11px] text-ink/50 font-sans mt-0.5 leading-tight">
                    {row.detail}
                  </p>
                </div>
              </div>
              <div className="col-span-4 px-7 py-5 flex items-start gap-3 border-l border-ink/5">
                <X size={16} strokeWidth={2.2} className="text-rose-500 shrink-0 mt-1" />
                <p className="font-sans text-sm text-ink/70 leading-snug">
                  {row.classique}
                </p>
              </div>
              <div className="col-span-4 px-7 py-5 flex items-start gap-3 border-l border-ink/5 bg-gold/[0.04]">
                <Check size={16} strokeWidth={2.4} className="text-gold shrink-0 mt-1" />
                <p className="font-serif text-sm text-ink leading-snug font-semibold">
                  {row.nous}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-4">
        {COMPARISON.map((row, i) => {
          const Icon = row.icon;
          return (
            <div
              key={row.label}
              data-testid={`comparison-card-${i}`}
              className="bg-white border border-ink/5 rounded-2xl p-5 shadow-soft"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-sage/10 flex items-center justify-center shrink-0">
                  <Icon size={16} strokeWidth={1.6} className="text-sage" />
                </div>
                <p className="font-serif text-lg text-ink">{row.label}</p>
              </div>
              <p className="text-[11px] text-ink/55 mb-4 leading-relaxed">
                {row.detail}
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2 bg-cream/40 rounded-lg p-3">
                  <X size={14} strokeWidth={2.2} className="text-rose-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.25em] text-ink/50 font-semibold">
                      Conciergerie classique
                    </p>
                    <p className="text-sm text-ink/75 mt-0.5">{row.classique}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-gold/10 border border-gold/20 rounded-lg p-3">
                  <Check size={14} strokeWidth={2.4} className="text-gold shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.25em] text-gold font-semibold">
                      Terre et Mer
                    </p>
                    <p className="text-sm text-ink font-semibold mt-0.5">{row.nous}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* =====================================================================
   Page wrapper
   ===================================================================== */
const PourquoiNous = () => {
  return (
    <div data-testid="page-pourquoi-nous">
      <SEO
        title="Pourquoi Terre et Mer ? Comparatif vs autres conciergeries"
        description="Conciergerie classique 20-25 % vs Terre et Mer 15 %. Comparatif transparent en 10 critères + simulateur d'économies. Bretagne & Côte d'Émeraude, 24h/24, 7j/7."
        keywords="comparatif conciergerie Airbnb, conciergerie 15%, économie conciergerie, Bretagne, Côte d'Émeraude, frais conciergerie, simulateur"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Pourquoi nous", path: "/pourquoi-nous" },
        ]}
      />
      <PageHero
        overline="Pourquoi nous choisir"
        title="Pourquoi Terre et Mer"
        italic="vs les autres"
        description="Moins cher, plus rapide, plus humain. Voici en quoi notre approche change la vie des propriétaires de la Côte d'Émeraude, chiffres et critères concrets à l'appui."
        image="/images/pourquoi-nous-hero.jpg"
        cta={{ to: "/contact", label: "Lancer mon étude gratuite" }}
      />

      <ComparisonTable />
      <SavingsSimulator />

      {/* Bottom CTA */}
      <section
        className="py-20 lg:py-24 bg-cream border-t border-gold/20"
        data-testid="pourquoi-bottom-cta"
      >
        <div className="container-tm">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline inline-flex items-center gap-2 text-gold">
              <Sparkles size={12} strokeWidth={2} />
              Le bon choix
            </span>
            <h3 className="mt-5 font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Prêt à passer
              <span className="italic text-gold"> du côté lumineux</span> ?
            </h3>
            <p className="mt-6 font-sans text-base md:text-lg text-ink/65 leading-relaxed">
              Étude gratuite, sans engagement, réponse sous 24h. Notre City Manager local étudie votre logement et vous propose un plan personnalisé.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="btn-accent group"
                data-testid="pourquoi-cta-contact"
              >
                Étude gratuite 24h
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <Link
                to="/resultats"
                className="btn-secondary"
                data-testid="pourquoi-cta-resultats"
              >
                Voir nos résultats clients
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PourquoiNous;
